import type {
  SpeechRecognizer,
  SpeechRecognitionEventArgs,
  SpeechRecognitionCanceledEventArgs
} from 'microsoft-cognitiveservices-speech-sdk'
import EventListener from '@/services/EventListener/EventListener'
import { loadSpeechSdk } from './loadSpeechSdk'
import SpeechTokenManager from './SpeechTokenManager'
import { SPEECH_LANGUAGES, SpeechEvents, TOKEN_REFRESH_INTERVAL_MS } from './constants'
import type { ISpeechToTextService, SpeechEventMap } from './interfaces'

// Capture the mic ourselves with echo cancellation and noise suppression disabled:
// their adaptive gating intermittently crushes the signal to near-silence on some
// Android devices, which makes Azure return an empty transcript. Auto gain control
// stays on — it keeps a usable level (without it, level drops on other devices).
const AUDIO_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: true,
    channelCount: 1
  }
}

class SpeechToTextService extends EventListener<SpeechEventMap> implements ISpeechToTextService {
  private recognizer: SpeechRecognizer | null = null
  private stream: MediaStream | null = null
  private tokenManager = new SpeechTokenManager()
  private refreshTimer: ReturnType<typeof setInterval> | undefined
  private active = false

  async start(): Promise<void> {
    if (this.active) {
      return
    }
    this.active = true
    // Tracked separately from this.recognizer so a stop() that lands during the
    // awaits below can never leave a recognizer running (prod-only race: the SDK
    // chunk + token fetch make the start window long enough to release mid-flight).
    let created: SpeechRecognizer | null = null
    try {
      const sdk = await loadSpeechSdk()
      if (!this.active) {
        return
      }
      const { token, region } = await this.tokenManager.getValidToken()
      if (!this.active) {
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS)
      this.stream = stream
      if (!this.active) {
        this.stopStream()
        return
      }

      const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(token, region)
      speechConfig.setProperty(sdk.PropertyId.SpeechServiceConnection_LanguageIdMode, 'Continuous')
      const autoDetect = sdk.AutoDetectSourceLanguageConfig.fromLanguages([...SPEECH_LANGUAGES])
      const audioConfig = sdk.AudioConfig.fromStreamInput(stream)

      const recognizer = sdk.SpeechRecognizer.FromConfig(speechConfig, autoDetect, audioConfig)
      created = recognizer
      if (!this.active) {
        recognizer.close()
        this.stopStream()
        return
      }

      recognizer.recognizing = (_recognizer, event: SpeechRecognitionEventArgs) => {
        if (event.result.text) {
          this.emit(SpeechEvents.recognizing, event.result.text)
        }
      }
      recognizer.recognized = (_recognizer, event: SpeechRecognitionEventArgs) => {
        if (event.result.reason === sdk.ResultReason.RecognizedSpeech && event.result.text) {
          this.emit(SpeechEvents.recognized, event.result.text)
        }
      }
      recognizer.canceled = (_recognizer, event: SpeechRecognitionCanceledEventArgs) => {
        this.emit(SpeechEvents.error, event.errorDetails || 'canceled')
      }
      recognizer.sessionStarted = () => {
        if (this.active) {
          this.emit(SpeechEvents.started)
        }
      }

      await new Promise<void>((resolve, reject) => {
        recognizer.startContinuousRecognitionAsync(() => resolve(), error => reject(error))
      })

      if (!this.active) {
        await this.disposeRecognizer(recognizer)
        return
      }

      this.recognizer = recognizer
      this.startTokenRefresh()
    } catch (error) {
      this.active = false
      if (created) {
        await this.disposeRecognizer(created)
      } else {
        this.stopStream()
      }
      this.recognizer = null
      this.emit(SpeechEvents.error, error instanceof Error ? error.message : String(error))
      throw error
    }
  }

  async stop(): Promise<void> {
    if (!this.active) {
      return
    }
    this.active = false
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = undefined
    }
    const recognizer = this.recognizer
    this.recognizer = null
    if (recognizer) {
      await this.disposeRecognizer(recognizer)
    } else {
      this.stopStream()
    }
    this.emit(SpeechEvents.stopped)
  }

  private startTokenRefresh() {
    this.refreshTimer = setInterval(async () => {
      try {
        const { token } = await this.tokenManager.getValidToken()
        if (this.recognizer) {
          this.recognizer.authorizationToken = token
        }
      } catch {
        // Keep the current session on the existing token; a failed refresh
        // shouldn't abort an in-progress recognition.
      }
    }, TOKEN_REFRESH_INTERVAL_MS)
  }

  private async disposeRecognizer(recognizer: SpeechRecognizer) {
    try {
      await new Promise<void>(resolve => {
        recognizer.stopContinuousRecognitionAsync(() => resolve(), () => resolve())
      })
    } catch {
      // Ignore stop errors during disposal.
    }
    try {
      recognizer.close()
    } catch {
      // Ignore close errors during disposal.
    }
    this.stopStream()
  }

  private stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
  }
}

export default SpeechToTextService
